import { LoadingSpinner } from "components/LoadingSpinner";
import { UploadFile } from "components/UploadFile";
import { ToastContext } from "context/ToastContext";
import { useUserWithAccount } from "context/UserAccountProvider";
import dayjs from "dayjs";
import { parseDate } from "helpers/date";
import { centsToDollars, parsePrice } from "helpers/price";
import { sizer } from "layout/styles/styled/sizer";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { Menu } from "primereact/menu";
import { MenuItem } from "primereact/menuitem";
import { useContext, useMemo, useRef, useState, useCallback } from "react";
import {
  useGenerateNewReportMutation,
  useGetReportsQuery,
  useGetReportByIdQuery,
  useReplaceReportDocumentMutation,
  usePublishReportMutation,
  useUnpublishReportMutation,
  useMarkReportDownloadedMutation,
} from "store/queries/report";
import styled from "styled-components";

import { MonthlyReportModal } from "../MonthlyReportModal";
import { PaidOrdersDownload } from "../PaidOrdersDownload";

const StyledDialog = styled(Dialog)`
  width: 50vw;

  @media (max-width: 720px) {
    width: 90vw;
  }
`;

const DialogContainer = styled.div`
  gap: ${sizer(4)};
  display: flex;
  align-items: center;
  flex-direction: column;
`;

const ErrorMessage = styled.p`
  color: #f44336;
  margin-top: ${sizer(2)};
  margin-bottom: ${sizer(2)};
  text-align: center;
  width: 100%;
`;

// Custom styling for published rows
const PublishedRowStyle = styled.div<{ showCondensedVersion: boolean }>`
  ${({ showCondensedVersion }) =>
    !showCondensedVersion &&
    `
      .published-row {
        background-color: #ecf7e9 !important;
      }
    `}
`;

// Define type for report row
interface IReportRow {
  id: string;
  accountName: string;
  reportDate: string;
  reportAmount: string | number;
  reportType: string;
  stripePayoutAmount: string | number;
  stripePayoutId: string | null;
  stripeAccountNumber: string | null;
  signedUrl: string;
  originalFilename: string;
  publishedAt: string | null;
  downloadedAt: string | null;
  downloadedByUser: { name: string } | null;
}

export function ReportsTable({ accountId }: { accountId?: string }) {
  const {
    data,
    isFetching,
    isError,
    refetch: refetchTable,
  } = useGetReportsQuery({ accountId });
  const showCondensedVersion = !!accountId;
  const { userInfo } = useUserWithAccount();
  // Gated on analyticsTabEnabled, not just accountId: this export previously
  // lived on the Analytics tab, and the Reports tab reaches a wider audience
  // (e.g. reportsOnlyMode staff, who cannot see Analytics at all).
  const showPaidOrdersDownload = !!accountId && !!userInfo?.analyticsTabEnabled;

  const { current: toastElement } = useContext(ToastContext);
  const menuRef = useRef<Menu>(null);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);

  const [selectedFile, setSelectedFile] = useState({} as File);
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [selectedReportForReplace, setSelectedReportForReplace] =
    useState<IReportRow | null>(null);
  const [replaceFileToUpload, setReplaceFileToUpload] = useState({} as File);
  const [filenameError, setFilenameError] = useState<string | null>(null);
  const [reportIdToPublish, setReportIdToPublish] = useState<string | null>(
    null
  );

  const [selectAccountDialog, setSelectAccountDialog] = useState(false);

  const [mutation, { isLoading: isGeneratingNewReport }] =
    useGenerateNewReportMutation();

  const [replaceDocumentMutation, { isLoading: isReplacingDocument }] =
    useReplaceReportDocumentMutation();

  const [publishReportMutation] = usePublishReportMutation();

  const [unpublishReportMutation] = useUnpublishReportMutation();

  const [markDownloadedMutation] = useMarkReportDownloadedMutation();

  // Use the hook at the top level, but skip the query when no report ID is set
  const { data: reportDetailsData, isFetching: isFetchingReportDetails } =
    useGetReportByIdQuery(reportIdToPublish || "", {
      skip: !reportIdToPublish,
    });

  const reportTypes = {
    STANDARD: "STANDARD",
    PLINE: "PLINE",
  };

  const handleOnChangeAccountDialogVisibility = () => {
    setSelectAccountDialog((prevState) => !prevState);
  };

  const reportInfo = useMemo(() => {
    if (isError || !data) return [];

    return data?.data.map((currentReport) => ({
      id: currentReport.id,
      accountName: currentReport?.name || currentReport?.provider?.name,
      reportDate: parseDate(currentReport?.reportDate),
      reportAmount: currentReport?.reportAmountInCents
        ? centsToDollars(currentReport?.reportAmountInCents)
        : "-",
      reportType: currentReport.reportType,
      stripePayoutAmount: currentReport?.stripePayout?.amountInCents
        ? centsToDollars(currentReport?.stripePayout?.amountInCents)
        : "",
      stripePayoutId: currentReport?.stripePayout?.id,
      stripeAccountNumber: currentReport?.provider?.stripeAccountNumber,
      signedUrl: currentReport?.signedUrl,
      originalFilename: currentReport?.s3Key
        ? currentReport?.s3Key.split("/").pop()
        : "",
      publishedAt: currentReport?.publishedAt,
      downloadedAt: currentReport?.downloadedAt,
      downloadedByUser: currentReport?.downloadedByUser,
    }));
  }, [data]);

  const statusRowFilterTemplate = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        placeholder="Select a Status"
        className="p-column-filter"
        options={Object.values(reportTypes)}
        onChange={(e) => options.filterApplyCallback(e.value)}
      />
    );
  };

  const priceFilterTemplate = (options: any) => {
    return (
      <InputNumber
        locale="en-US"
        currency="USD"
        mode="currency"
        value={options.value}
        placeholder="$1234,00"
        onChange={(e) => options.filterApplyCallback(e.value, options.index)}
      />
    );
  };

  const stripePayoutTemplate = (
    stripePayoutId: string | null,
    stripePayoutAmountInCents: number | null,
    stripeAccountNumber: string | null
  ) => {
    if (!stripePayoutId || !stripePayoutAmountInCents) return null;

    return (
      <a
        href={`https://dashboard.stripe.com/${stripeAccountNumber}/payouts/${stripePayoutId}`}
        target="_blank"
        rel="noreferrer"
      >
        {parsePrice(stripePayoutAmountInCents)}
      </a>
    );
  };

  const handlePublishReport = useCallback(
    (reportId: string) => {
      publishReportMutation({ reportId })
        .unwrap()
        .then(() => {
          setReportIdToPublish(null);

          toastElement?.show({
            severity: "success",
            summary: "Success!",
            detail: "Report has been published to client.",
          });

          // No need to call refetchTable() as the store
          // should be updated automatically
        })
        .catch((error) => {
          // Extract error message from API response format
          let errorMessage = "Try again later.";
          if (
            error?.data?.messages &&
            Array.isArray(error.data.messages) &&
            error.data.messages.length > 0
          ) {
            [errorMessage] = error.data.messages;
          } else if (error?.data?.message) {
            errorMessage = error.data.message;
          }

          toastElement?.show({
            severity: "error",
            summary: "Publishing failed",
            detail: errorMessage,
          });
        });
    },
    [publishReportMutation, toastElement]
  );

  // Effect to show confirmation dialog when report details are loaded
  useMemo(() => {
    if (reportIdToPublish && !isFetchingReportDetails) {
      // If we have report data, show the confirmation with emails
      if (reportDetailsData) {
        const emailList = reportDetailsData?.data?.emailList || [];
        const confirmMessage = (
          <div>
            <p>Are you sure you want to publish this report to the client?</p>
            <br />
            <p>The report will be sent to the following email addresses:</p>
            {emailList.length > 0 ? (
              <ul
                style={{
                  marginTop: "10px",
                  marginLeft: "15px",
                }}
              >
                {emailList.map((email, index) => (
                  <li key={index}>{email.emailAddress}</li>
                ))}
              </ul>
            ) : (
              <p>
                <br />
                <em>No email recipients configured</em>
              </p>
            )}
          </div>
        );

        confirmDialog({
          message: confirmMessage,
          header: "Publish Report",
          icon: "pi pi-send",
          acceptClassName: "p-button-success",
          acceptLabel: "Publish",
          rejectLabel: "Cancel",
          accept: () => handlePublishReport(reportIdToPublish),
          reject: () => setReportIdToPublish(null),
        });
      } else {
        // Show a loading dialog that will be automatically replaced when data loads
        confirmDialog({
          message: (
            <div>
              <p>Loading report details...</p>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "20px",
                }}
              >
                <i
                  className="pi pi-spin pi-spinner"
                  style={{ fontSize: "2rem" }}
                />
              </div>
            </div>
          ),
          header: "Publish Confirmation",
          icon: "pi pi-info-circle",
          acceptClassName: "p-button-success",
          accept: undefined,
          reject: () => setReportIdToPublish(null),
        });
      }
    }
  }, [
    reportIdToPublish,
    reportDetailsData,
    isFetchingReportDetails,
    handlePublishReport,
  ]);

  const confirmPublish = (reportId: string) => {
    // Set the report ID to publish, which will trigger the fetch
    setReportIdToPublish(reportId);
  };

  const handleUnpublishReport = useCallback(
    (reportId: string) => {
      unpublishReportMutation({ reportId })
        .unwrap()
        .then(() => {
          toastElement?.show({
            severity: "success",
            summary: "Success!",
            detail: "Report has been unpublished. You can now replace it.",
          });
        })
        .catch((error) => {
          // Extract error message from API response format
          let errorMessage = "Try again later.";
          if (
            error?.data?.messages &&
            Array.isArray(error.data.messages) &&
            error.data.messages.length > 0
          ) {
            [errorMessage] = error.data.messages;
          } else if (error?.data?.message) {
            errorMessage = error.data.message;
          }

          toastElement?.show({
            severity: "error",
            summary: "Unpublishing failed",
            detail: errorMessage,
          });
        });
    },
    [unpublishReportMutation, toastElement]
  );

  const confirmUnpublish = (reportId: string) => {
    confirmDialog({
      message: (
        <div>
          <p>Are you sure you want to unpublish this report?</p>
          <br />
          <p>
            It will be hidden from the client until you publish it again, and
            you will be able to replace the document.
          </p>
          <br />
          <p>
            <em>
              Recipients who were already emailed will not be emailed again when
              you republish.
            </em>
          </p>
        </div>
      ),
      header: "Unpublish Report",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      acceptLabel: "Unpublish",
      rejectLabel: "Cancel",
      accept: () => handleUnpublishReport(reportId),
    });
  };

  const handleActionsClick = (event: React.MouseEvent, row: IReportRow) => {
    event.preventDefault();
    event.stopPropagation();

    if (menuRef.current) {
      const items: MenuItem[] = [
        {
          label: "Download",
          icon: "pi pi-download",
          command: async () => {
            // Mark the report as downloaded
            try {
              await markDownloadedMutation({ reportId: row.id }).unwrap();
              // Refetch reports to update the table
              await refetchTable();
              // Open the download in a new tab after marking as downloaded
              window.open(row.signedUrl);
            } catch (error) {
              // If marking as downloaded fails, still allow the download
              console.error("Failed to mark report as downloaded:", error);
              window.open(row.signedUrl);
            }
          },
        },
      ];
      if (!showCondensedVersion) {
        items.push(
          {
            label: "Replace",
            icon: "pi pi-refresh",
            command: () => {
              setSelectedReportForReplace(row);
              setFilenameError(null);
            },
            disabled: !!row.publishedAt, // Disable if already published
          },
          {
            label: row.publishedAt
              ? `Unpublish (published ${dayjs(row.publishedAt).format(
                  "MM/DD"
                )})`
              : "Publish to Client",
            icon: row.publishedAt ? "pi pi-undo" : "pi pi-send",
            command: () => {
              if (row.publishedAt) {
                confirmUnpublish(row.id);
              } else {
                confirmPublish(row.id);
              }
            },
          }
        );
        // Only show Upload option for PLINE reports
        if (row.reportType === reportTypes.PLINE) {
          items.splice(1, 0, {
            label: "Upload",
            icon: "pi pi-upload",
            command: () => {
              setSelectedReportId(row.id);
            },
            disabled: !!row.publishedAt, // Disable if already published
          });
        }
      }

      // Update menu items state
      setMenuItems(items);
      menuRef.current.toggle(event);
    }
  };

  const actionsTemplate = (row: IReportRow) => {
    return (
      <div className="actions-column">
        <Button
          icon="pi pi-ellipsis-v"
          className="p-button-rounded p-button-text"
          onClick={(e) => handleActionsClick(e, row)}
          aria-label="Actions"
        />
      </div>
    );
  };

  const handleOnClickToGenerateReport = () => {
    mutation({ reportId: selectedReportId, file: selectedFile })
      .unwrap()
      .then(() => {
        refetchTable();

        toastElement?.show({
          severity: "success",
          summary: "Success!",
          detail: `Your report has been updated successfully.`,
        });

        setSelectedFile({} as File);
        setSelectedReportId(null);
      })
      .catch(() => {
        toastElement?.show({
          severity: "error",
          summary: "Something went wrong.",
          detail: `Try again later.`,
        });
      });
  };

  const handleFileForReplaceSelected = (file: File) => {
    setReplaceFileToUpload(file);

    // Client-side validation for filename match
    if (!file || !file.name) {
      // Don't display an error if no file is selected yet
      setFilenameError(null);
      return;
    }

    if (
      selectedReportForReplace &&
      file.name !== selectedReportForReplace.originalFilename
    ) {
      setFilenameError(
        `Filename mismatch. Expected: ${selectedReportForReplace.originalFilename}, but got: ${file.name}`
      );
    } else {
      setFilenameError(null);
    }
  };

  const handleReplaceDocument = () => {
    if (!selectedReportForReplace) {
      return;
    }

    // Client-side validation before submission
    if (filenameError) {
      toastElement?.show({
        severity: "error",
        summary: "Filename mismatch",
        detail: filenameError,
      });
      return;
    }

    replaceDocumentMutation({
      reportId: selectedReportForReplace.id,
      file: replaceFileToUpload,
    })
      .unwrap()
      .then(() => {
        refetchTable();

        toastElement?.show({
          severity: "success",
          summary: "Success!",
          detail: `Your report document has been replaced successfully.`,
        });

        setReplaceFileToUpload({} as File);
        setSelectedReportForReplace(null);
        setFilenameError(null);
      })
      .catch((error) => {
        // Extract error message from API response format
        let errorMessage = "Try again later.";
        if (
          error?.data?.messages &&
          Array.isArray(error.data.messages) &&
          error.data.messages.length > 0
        ) {
          [errorMessage] = error.data.messages;
        } else if (error?.data?.message) {
          errorMessage = error.data.message;
        }

        toastElement?.show({
          severity: "error",
          summary: "Something went wrong.",
          detail: errorMessage,
        });
      });
  };

  // Function to determine row class based on published status
  const getRowClassName = (row: IReportRow) => {
    return row.publishedAt ? "published-row" : "";
  };

  if (isFetching) {
    return <LoadingSpinner />;
  }
  return (
    <PublishedRowStyle showCondensedVersion={showCondensedVersion}>
      <Menu model={menuItems} popup ref={menuRef} id="action-menu" />

      <StyledDialog
        draggable={false}
        header="Upload a new report"
        visible={!!selectedReportId}
        closable={!isGeneratingNewReport}
        onHide={() => {
          setSelectedReportId(null);
          setSelectedFile({} as File);
        }}
      >
        <DialogContainer>
          <UploadFile
            name="plineReport"
            disabled={isGeneratingNewReport}
            onSelectFile={setSelectedFile}
          />

          <Button
            loading={isGeneratingNewReport}
            onClick={handleOnClickToGenerateReport}
          >
            Upload and Generate New Reports
          </Button>
        </DialogContainer>
      </StyledDialog>

      <StyledDialog
        draggable={false}
        header="Replace Report Document"
        visible={!!selectedReportForReplace}
        closable={!isReplacingDocument}
        onHide={() => {
          setSelectedReportForReplace(null);
          setReplaceFileToUpload({} as File);
          setFilenameError(null);
        }}
      >
        <DialogContainer>
          {selectedReportForReplace && (
            <p>
              Please upload a file with the exact name:{" "}
              <strong>{selectedReportForReplace.originalFilename}</strong>
            </p>
          )}
          <UploadFile
            name="replaceReport"
            disabled={isReplacingDocument}
            onSelectFile={handleFileForReplaceSelected}
          />

          {filenameError && <ErrorMessage>{filenameError}</ErrorMessage>}

          <Button
            loading={isReplacingDocument}
            onClick={handleReplaceDocument}
            disabled={!!filenameError || !replaceFileToUpload.name}
          >
            {" "}
            Replace Report Document
          </Button>
        </DialogContainer>
      </StyledDialog>

      {!showCondensedVersion && (
        <>
          <MonthlyReportModal
            visible={selectAccountDialog}
            onClose={handleOnChangeAccountDialogVisibility}
          />
          <div className="flex justify-content-end mb-3">
            <Button
              type="button"
              icon="pi pi-book"
              label="Monthly Sales Reports"
              onClick={handleOnChangeAccountDialogVisibility}
            />
          </div>
        </>
      )}
      {showPaidOrdersDownload && (
        <>
          <PaidOrdersDownload accountId={accountId} />
          <Divider />
        </>
      )}
      <DataTable
        rows={10}
        paginator
        stripedRows
        value={reportInfo}
        className="p-fluid"
        filterDisplay="row"
        responsiveLayout="scroll"
        emptyMessage="No reports to show."
        rowClassName={getRowClassName}
      >
        <Column
          filter
          field="accountName"
          style={{ minWidth: "320px" }}
          header="Account/Provider Name"
          filterPlaceholder="John Smith"
        />
        <Column
          filter
          field="reportDate"
          header="Report Date"
          style={{ minWidth: "200px" }}
          filterPlaceholder="mm/dd/yyyy"
        />
        <Column
          filter
          field="stripePayoutAmount"
          style={{ minWidth: "200px" }}
          header="Stripe Payout Amount"
          body={(row) =>
            stripePayoutTemplate(
              row.stripePayoutId,
              row.stripePayoutAmount,
              row.stripeAccountNumber
            )
          }
        />
        {!showCondensedVersion && (
          <Column
            filter
            dataType="numeric"
            field="reportAmount"
            header="Report Amount"
            style={{ minWidth: "180px" }}
            filterElement={priceFilterTemplate}
            body={(row) => parsePrice(row.reportAmount)}
          />
        )}
        {!showCondensedVersion && (
          <Column
            filter
            field="reportType"
            header="Report Type"
            showFilterMenu={false}
            style={{ minWidth: "120px", maxWidth: "220px" }}
            filterElement={statusRowFilterTemplate}
          />
        )}
        <Column
          field="downloaded"
          header="Downloaded"
          style={{ minWidth: "200px" }}
          body={(row) =>
            row.downloadedAt && row.downloadedByUser
              ? `${dayjs(row.downloadedAt).format("M/D/YY h:mma")} by ${
                  row.downloadedByUser.name
                }`
              : ""
          }
        />
        <Column
          field="actions"
          header="Actions"
          style={{ maxWidth: "100px" }}
          body={actionsTemplate}
        />
      </DataTable>
    </PublishedRowStyle>
  );
}
